import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImage } from "@/lib/api";

const TAGS = [
  { id: "", label: "Toutes" },
  { id: "produits", label: "Produits" },
  { id: "blog", label: "Blog" },
  { id: "categories", label: "Catégories" },
  { id: "bannieres", label: "Bannières" },
  { id: "autres", label: "Autres" },
];

export default function MediaLibraryPicker({ open, onClose, onSelect, defaultTag = "" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState(defaultTag);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media", { params: { search, tag } });
      setItems(data);
    } catch {
      toast.error("Impossible de charger la bibliothèque");
    } finally {
      setLoading(false);
    }
  }, [search, tag]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [open, load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tag", tag || defaultTag || "autres");
      const { data } = await api.post("/admin/upload", fd);
      toast.success("Image ajoutée à la bibliothèque");
      onSelect(data.url);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="media-picker-overlay"
      className="fixed inset-0 z-[70] flex items-end justify-center bg-bark/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        data-testid="media-picker"
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl bg-cream shadow-2xl sm:max-h-[85vh] sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-amber-900/10 bg-white px-4 py-4 sm:px-6">
          <h2 className="font-serif text-lg font-semibold text-bark sm:text-xl">Bibliothèque d'images</h2>
          <button data-testid="media-picker-close" onClick={onClose} className="rounded-full p-2 text-bark-soft transition-colors hover:bg-sand">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col gap-3 border-b border-amber-900/10 bg-white px-4 py-3 sm:flex-row sm:items-center sm:px-6">
          <div className="relative flex-1">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-mute" />
            <input
              data-testid="media-picker-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou légende…"
              className="w-full rounded-full border border-amber-900/20 bg-cream py-2.5 pl-9 pr-4 text-sm outline-none focus:border-amber-deep"
            />
          </div>
          <label
            data-testid="media-picker-upload-label"
            className={`flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-amber-deep px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-honey ${uploading ? "opacity-60" : ""}`}
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
            {uploading ? "Envoi…" : "Téléverser"}
            <input ref={fileRef} data-testid="media-picker-upload-input" type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>

        <div className="flex gap-2 overflow-x-auto border-b border-amber-900/10 bg-white px-4 py-2 sm:px-6">
          {TAGS.map((t) => (
            <button
              key={t.id}
              data-testid={`media-picker-tag-${t.id || "all"}`}
              onClick={() => setTag(t.id)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${tag === t.id ? "bg-bark text-white" : "border border-amber-900/15 text-bark-soft hover:border-amber-deep"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-bark-soft">
              <Loader2 className="animate-spin text-amber-deep" size={22} /> Chargement…
            </div>
          ) : items.length === 0 ? (
            <p data-testid="media-picker-empty" className="py-16 text-center text-sm text-stone-mute">
              Aucune image. Téléversez-en une pour commencer.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {items.map((m) => (
                <button
                  key={m.id}
                  data-testid={`media-picker-item-${m.id}`}
                  onClick={() => { onSelect(m.url); onClose(); }}
                  className="group overflow-hidden rounded-xl border border-amber-900/10 bg-white text-left transition-all hover:border-amber-deep hover:shadow-md"
                >
                  <img src={resolveImage(m.url)} alt={m.caption || m.original_filename} className="aspect-square w-full object-cover" />
                  <p className="truncate px-2 py-1.5 text-xs text-bark-soft">{m.caption || m.original_filename}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
